import type { MoonWeekEntry } from '../types';

export default function MoonWeek({
  week,
  timeZone,
}: {
  week: MoonWeekEntry[];
  timeZone: string;
}) {
  if (!week.length) return null;
  return (
    <section>
      <h2 className="text-xs uppercase tracking-wider opacity-70 mb-2">Moon · next 7 days</h2>
      <div className="grid grid-cols-7 gap-1">
        {week.map((d) => (
          <div
            key={d.date}
            className="bg-white/10 backdrop-blur-sm rounded-lg p-2 text-center"
            title={`${d.moonPhaseName} · ${d.moonIllumPct}% illum`}
          >
            <div className="text-[10px] opacity-70">
              {new Date(d.date).toLocaleDateString('en-US', {
                weekday: 'short',
                timeZone,
              })}
            </div>
            <div className="my-1 flex justify-center">
              <MoonGlyph phase={d.moonPhase} />
            </div>
            <div className="text-[10px] tabular-nums opacity-80">{d.moonIllumPct}%</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// Minimal moon SVG: renders the illuminated slice for any phase 0..1.
// Approach: a full circle with a second ellipse that masks the shadow side.
function MoonGlyph({ phase }: { phase: number }) {
  const size = 28;
  const r = size / 2 - 1;
  const cx = size / 2;
  const cy = size / 2;
  // Illumination fraction on a 0..1 scale (waxing +, waning -)
  // Phase 0 = new (dark); 0.5 = full (bright); 0.25 waxing half; 0.75 waning half.
  const illumPortion = (1 - Math.cos(phase * 2 * Math.PI)) / 2;
  const waxing = phase < 0.5;
  // Terminator ellipse x-radius sweeps from -r (new) → 0 (half) → +r (full) → 0 → -r.
  const termRx = r * Math.cos(phase * 2 * Math.PI);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {/* Full moon outline */}
      <circle cx={cx} cy={cy} r={r} fill="#0f172a" stroke="#cbd5e1" strokeWidth="1" />
      {/* Illuminated half — always one side, depending on waxing/waning */}
      <path
        d={`M ${cx} ${cy - r}
            A ${r} ${r} 0 0 ${waxing ? 1 : 0} ${cx} ${cy + r}
            A ${Math.abs(termRx)} ${r} 0 0 ${termRx > 0 === waxing ? 0 : 1} ${cx} ${cy - r}
            Z`}
        fill="#f1f5f9"
      />
      {/* At new moon illumPortion is near zero — subtle indicator so the tile isn't blank */}
      {illumPortion < 0.02 && (
        <circle cx={cx} cy={cy} r={1.2} fill="#94a3b8" />
      )}
    </svg>
  );
}
